import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Calculator, BookOpen } from 'lucide-react';
import ScrollToTop from '@/components/ScrollToTop';

function SignInCTA() {
  const { pathname } = useLocation();
  const [dismissed, setDismissed] = React.useState(
    () => sessionStorage.getItem('bm_cta_dismissed') === '1'
  );

  if (dismissed || pathname === '/login' || pathname === '/signup') return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-stone-900 border-t border-stone-700 px-4 py-3 flex items-center justify-between gap-4">
      <p className="text-sm text-stone-300 flex items-center gap-2 min-w-0 truncate">
        <span className="text-orange-400 shrink-0">✦</span>
        <span>Unlock personalized insights — track spending, goals & EMIs free</span>
      </p>
      <div className="flex items-center gap-3 shrink-0">
        <Link to="/login" className="text-sm font-semibold text-stone-300 hover:text-white transition-colors whitespace-nowrap">Sign in →</Link>
        <Link to="/signup" className="text-sm font-bold bg-orange-500 hover:bg-orange-600 text-white px-4 py-1.5 rounded-lg transition-colors whitespace-nowrap">Get started free</Link>
        <button
          onClick={() => { sessionStorage.setItem('bm_cta_dismissed', '1'); setDismissed(true); }}
          className="text-stone-500 hover:text-stone-300 transition-colors"
          aria-label="Dismiss"
        >✕</button>
      </div>
    </div>
  );
}

export default function PublicShell({ children, wide = false }) {
  const { pathname } = useLocation();
  const isTools = pathname.startsWith('/tools');
  const isLearn = pathname.startsWith('/learn');

  return (
    <div className="min-h-screen bg-stone-50 dark:bg-stone-950">
      {/* Topbar */}
      <header className="sticky top-0 z-40 h-14 bg-white dark:bg-stone-900 border-b border-stone-200 dark:border-stone-800 flex items-center px-4 md:px-8 gap-4 overflow-hidden">
        <Link to="/" className="flex items-center gap-2 font-extrabold text-orange-500 text-lg tracking-tight shrink-0">
          BudgetMantra
        </Link>
        <div className="hidden md:flex items-center gap-1 ml-4 min-w-0 overflow-hidden">
          <Link to="/tools"
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors shrink-0 ${isTools ? 'bg-indigo-50 text-indigo-600 dark:bg-indigo-950/40 dark:text-indigo-400' : 'text-stone-500 hover:text-stone-800 dark:text-stone-400 dark:hover:text-stone-200'}`}>
            <Calculator size={14} /> Calculators
          </Link>
          {/* TEMP: Journeys disabled — buggy, re-enable once stable */}
          {/* <Link to="/journeys"
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors shrink-0 ${pathname.startsWith('/journeys') ? 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400' : 'text-stone-500 hover:text-stone-800 dark:text-stone-400 dark:hover:text-stone-200'}`}>
            ✦ Journeys
          </Link> */}
          <Link to="/learn"
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors shrink-0 ${isLearn ? 'bg-indigo-50 text-indigo-600 dark:bg-indigo-950/40 dark:text-indigo-400' : 'text-stone-500 hover:text-stone-800 dark:text-stone-400 dark:hover:text-stone-200'}`}>
            <BookOpen size={14} /> Learn
          </Link>
        </div>
        <span className="flex-1" />
        <Link to="/login" className="text-sm font-semibold text-stone-600 dark:text-stone-300 hover:text-orange-500 transition-colors shrink-0">
          Sign in
        </Link>
        <Link to="/signup" className="text-sm font-bold bg-orange-500 hover:bg-orange-600 text-white px-4 py-1.5 rounded-lg transition-colors shrink-0">
          Get started free
        </Link>
      </header>

      {/* Page content — wide for calc/learn, capped for old pages */}
      <main className={`pb-16 ${wide ? '' : 'max-w-4xl mx-auto px-4 py-8'}`}>{children}</main>

      {/* Footer */}
      <footer className="border-t border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 mt-16 px-4 md:px-8 py-8">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between gap-6 text-sm text-stone-400">
          <div>
            <p className="font-extrabold text-stone-700 dark:text-stone-200 mb-1">BudgetMantra</p>
            <p>Free financial tools for India</p>
          </div>
          <div className="flex flex-wrap gap-6">
            <div>
              <p className="font-semibold text-stone-600 dark:text-stone-300 mb-2">Calculators</p>
              <div className="space-y-1">
                {[['EMI Calculator','/tools/emi-calculator'],['SIP Calculator','/tools/sip-calculator'],['Income Tax','/tools/income-tax-calculator'],['Salary Calculator','/tools/salary-calculator']].map(([l,p]) => (
                  <Link key={p} to={p} className="block hover:text-indigo-500 transition-colors">{l}</Link>
                ))}
              </div>
            </div>
            <div>
              <p className="font-semibold text-stone-600 dark:text-stone-300 mb-2">More Tools</p>
              <div className="space-y-1">
                {[['FIRE Calculator','/tools/fire-calculator'],['EPF Calculator','/tools/epf-calculator'],['Lumpsum Calculator','/tools/lumpsum-calculator'],['XIRR Calculator','/tools/xirr-calculator']].map(([l,p]) => (
                  <Link key={p} to={p} className="block hover:text-indigo-500 transition-colors">{l}</Link>
                ))}
              </div>
            </div>
            <div>
              <p className="font-semibold text-stone-600 dark:text-stone-300 mb-2">Company</p>
              <div className="space-y-1">
                <Link to="/privacy" className="block hover:text-indigo-500">Privacy Policy</Link>
                <Link to="/terms" className="block hover:text-indigo-500">Terms of Service</Link>
                <Link to="/signup" className="block hover:text-indigo-500">Sign Up Free</Link>
              </div>
            </div>
          </div>
        </div>
        <p className="max-w-7xl mx-auto mt-6 text-xs text-stone-400">
          All calculations are estimates for planning purposes. Consult a financial advisor for personalised advice. Chanakya AI is powered by Anthropic Claude.
        </p>
      </footer>

      <SignInCTA />
      <ScrollToTop />
    </div>
  );
}
